import {
    Button,
    Form,
    Modal,
    Tab,
    toInt,
    useErrorState,
    usePendingState,
} from '@finbeat/react-toolkit';
import { updateTariffPartial } from 'axios/tariffsApi';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { TariffPeriodUpdatePartial, TariffsInfo, TariffUpdatePartial } from 'models/api/tariff';
import { TariffInfoProvider, useTariffInfoState } from 'providers/TariffInfoProvider';
import { Key, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { ModalBody, ModalFooter, ModalHeader, Nav, NavItem, NavLink, TabContent } from 'reactstrap';
import { useTariffsWorkplaceState } from '../Tariffs';
import './TariffsModal.scss';
import TariffsModalMain from './TariffsModalMain';
import TariffsModalPeriods from './TariffsModalPeriods';
import TariffsModalSchedule from './TariffsModalSchedule';

export type TariffAccesses = {
    fullAccess: boolean;
    partialAccess?: boolean;
};

type TariffModalProps = {
    isOpen: boolean;
    toggle: () => void;
    queryKey: string[];
};

const TariffsModalContent = ({ isOpen = false, toggle, queryKey }: TariffModalProps) => {
    const api = useApi();
    const { hasRole, auth } = useAuth();
    const { t } = useTranslation();
    const { setPending } = usePendingState();
    const { handleRequestError } = useErrorState();
    const queryClient = useQueryClient();
    const { currentItem, changeItemMutation, addItemMutation } = useTariffsWorkplaceState();
    const { coverGroups, dicts } = useTariffInfoState();

    const formRef = useRef<HTMLFormElement | null>(null);
    const [activeTab, setActiveTab] = useState('main');

    const saveChanges = (data: TariffsInfo) => {
        if (fullAccess) {
            data.id
                ? changeItemMutation.mutate(data, { onSuccess: () => toggle() })
                : addItemMutation.mutate(data, { onSuccess: () => toggle() });
        } else {
            setPending(true);
            const periods =
                data.accessType === 'PartialAccess'
                    ? data.periods.reduce<TariffPeriodUpdatePartial[]>((res, item) => {
                        item.parentServices.forEach((x) => {
                            const formulas = x.services.filter((s) =>
                                data.editableFields?.some((x) => x === s.serviceId)
                            );
                            if (formulas)
                                res.push({
                                    periodId: x.id,
                                    type: x.type,
                                    parentServiceId: x.parentServiceId,
                                    services: formulas,
                                });
                        });
                        return res;
                    }, [])
                    : [];
            updatePartial.mutate({
                id: data.id,
                tariffPlanId: data.tariffPlanId,
                name: data.name,
                apr: data.apr,
                dsc: data.dsc,
                periods: periods,
            });
        }
    };

    const updatePartial = useMutation(
        'updateTariffPartial',
        (info: TariffUpdatePartial) => updateTariffPartial(api, info),
        {
            onSuccess: () => {
                setPending(false);
                toggle();

                queryClient.invalidateQueries([queryKey]);
            },
            onError: (e) => handleRequestError(e),
        }
    );

    const toggleTab = (value?: Key) => {
        setActiveTab(value?.toString() ?? '');
    };

    const initialItem = useMemo<TariffsInfo>(
        () => ({
            tariffPlanId: null,
            isActive: false,
            name: '',
            creditProuctTemplateId: null,
            tariffPlanType: '',
            apr: null,
            coverGroupId: toInt(coverGroups[0]?.id) ?? null,
            termUnits: dicts.termUnits[0]?.id?.toString() ?? '',
            minTerm: null,
            maxTerm: null,
            dsc: '',
            accessType: auth?.tariffEditingAccess ?? '',
            editableFields: auth?.tariffEditableBillServices?.map((x) => parseInt(x)) ?? null,

            periods: [],
        }),
        [coverGroups, dicts.termUnits]
    );

    const defaultValues = useMemo<TariffsInfo>(
        () => currentItem ?? initialItem,
        [currentItem, initialItem]
    );

    const fullAccess = currentItem ? hasRole('tariffsAdd') : hasRole('tariffsUpdate');
    const partialAccess = !!(currentItem && hasRole('tariffsUpdatePartial'));

    return (
        <Modal size="xl" isOpen={isOpen} toggle={toggle} scrollable className="tariffs-modal">
            <ModalHeader toggle={toggle}>
                {defaultValues.id ? `#${defaultValues.id}` : t('BackOfficeNew')}
            </ModalHeader>
            <ModalBody className="custom-scrollbar">
                <Nav className="mb-3" tabs>
                    <NavItem>
                        <NavLink
                            href="#"
                            onClick={() => setActiveTab('main')}
                            active={activeTab === 'main'}
                        >
                            {t('BackOfficeMainPageName')}
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            href="#"
                            onClick={() => setActiveTab('periods')}
                            active={activeTab === 'periods'}
                        >
                            {t('BackOfficePeriods')}
                        </NavLink>
                    </NavItem>
                    {defaultValues.id && (
                        <NavItem>
                            <NavLink
                                href="#"
                                onClick={() => setActiveTab('schedule')}
                                active={activeTab === 'schedule'}
                            >
                                {t('BackOfficeSchedule')}
                            </NavLink>
                        </NavItem>
                    )}
                </Nav>
                <Form<TariffsInfo>
                    onSubmit={(data) => saveChanges(data)}
                    defaultValues={defaultValues}
                    clearAfterSubmit={false}
                    className="form-in-modal"
                    innerRef={formRef}
                >
                    <TabContent activeTab={activeTab}>
                        <Tab toggle={toggleTab} tabId="main">
                            <TariffsModalMain
                                fullAccess={fullAccess}
                                partialAccess={partialAccess}
                            />
                        </Tab>
                        <Tab toggle={toggleTab} tabId="periods">
                            <TariffsModalPeriods fullAccess={fullAccess} />
                        </Tab>
                    </TabContent>
                </Form>
                <TabContent activeTab={activeTab}>
                    {defaultValues.id && (
                        <Tab toggle={toggleTab} tabId="schedule">
                            <TariffsModalSchedule
                                tariffPlanId={defaultValues.tariffPlanId}
                                apr={defaultValues.apr}
                            />
                        </Tab>
                    )}
                </TabContent>
            </ModalBody>
            <ModalFooter>
                <Button
                    color="secondary"
                    type="button"
                    disabled={!fullAccess && !partialAccess}
                    onClick={toggle}
                >
                    <Trans i18nKey="BackOfficeCancel" />
                </Button>
                <Button color="success" onClick={() => formRef.current?.requestSubmit()}>
                    <Trans i18nKey="BackOfficeSave" />
                </Button>
            </ModalFooter>
        </Modal>
    );
};

const TariffsModal = (props: TariffModalProps) => {
    return (
        <TariffInfoProvider>
            <TariffsModalContent {...props} />
        </TariffInfoProvider>
    );
};

export default TariffsModal;
